import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, ExternalLink, GitPullRequest, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PRMergeConfirmDialog from "@/components/tickets/PRMergeConfirmDialog";

const FILTERS = [
  { id: "all", label: "All PRs" },
  { id: "open", label: "Awaiting merge" },
  { id: "merged", label: "Merged" },
];

function exportPrTicketsCsv(tickets) {
  const columns = [
    ["Ticket", "ticket_number"],
    ["PR reference", "pr_reference"],
    ["PR URL", "pr_url"],
    ["PR status", "pr_status"],
    ["Raised by", "pr_raised_by_name"],
    ["Raised at", "pr_raised_at"],
    ["Merged at", "pr_merged_at"],
    ["Deployment reference", "deployment_reference"],
    ["Release", "deployment_release_name"],
    ["Deployed at", "deployment_at"],
  ];
  const escapeCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const csv = [
    columns.map(([label]) => escapeCell(label)).join(","),
    ...tickets.map((ticket) => columns.map(([, key]) => escapeCell(ticket[key])).join(",")),
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "pr-tickets.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function PRAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mergingId, setMergingId] = useState("");
  const [mergeCandidate, setMergeCandidate] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/pr/tickets");
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load PR tickets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const visibleTickets = useMemo(
    () => filter === "all" ? tickets : tickets.filter((ticket) => ticket.pr_status === filter),
    [tickets, filter],
  );
  const openCount = tickets.filter((ticket) => ticket.pr_status === "open").length;
  const mergedCount = tickets.filter((ticket) => ticket.pr_status === "merged").length;

  const markMerged = async (ticket) => {
    setMergingId(ticket.id);
    try {
      await api.post(`/tickets/${ticket.id}/pr/merge`, { merged: true });
      setMergeCandidate(null);
      toast.success(`${ticket.pr_reference} marked as merged`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update PR status.");
    } finally {
      setMergingId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            PR Administration - overview
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
            Welcome back, <span className="gold-text">{user?.name || "User"}</span>.
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pull request status, merge progress, and deployment details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportPrTicketsCsv(visibleTickets)} disabled={visibleTickets.length === 0}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {[
          ["Total PRs", tickets.length, "text-primary", GitPullRequest, "all"],
          ["Awaiting merge", openCount, "text-amber-700", RefreshCw, "open"],
          ["Merged", mergedCount, "text-emerald-700", ExternalLink, "merged"],
        ].map(([label, value, tone, Icon, targetFilter]) => (
          <button
            key={label}
            type="button"
            data-testid={`pr-kpi-${targetFilter}`}
            aria-pressed={filter === targetFilter}
            onClick={() => setFilter(targetFilter)}
            className={`group rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${filter === targetFilter ? "border-primary/50 bg-accent/30" : ""}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
              <Icon className={`h-4 w-4 ${tone}`} />
            </div>
            <div className={`mt-2 font-display text-3xl font-bold ${tone}`}>{value}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter PR tickets">
        {FILTERS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="sm"
            variant={filter === item.id ? "default" : "outline"}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {error && <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}
      {loading && tickets.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading PR queue...</div>
      ) : visibleTickets.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center">
          <GitPullRequest className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-medium">No {filter === "all" ? "PRs" : `${filter} PRs`} found</p>
          <p className="mt-1 text-sm text-muted-foreground">PRs appear here after a PR reference is recorded.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Recent pull requests</div>
            <span className="text-xs text-muted-foreground">{visibleTickets.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left">Ticket</th>
                  <th className="px-3 py-2 text-left">PR reference</th>
                  <th className="px-3 py-2 text-left">PR status</th>
                  <th className="px-3 py-2 text-left">Raised by</th>
                  <th className="px-3 py-2 text-left">Raised</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleTickets.map((ticket) => {
                  const merged = ticket.pr_status === "merged";
                  return (
                    <tr
                      key={ticket.id}
                      onClick={() => navigate(`/pr/tickets/${ticket.id}`)}
                      className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-muted-foreground">
                        <Link to={`/pr/tickets/${ticket.id}`} className="hover:text-primary" onClick={(event) => event.stopPropagation()}>{ticket.ticket_number}</Link>
                      </td>
                      <td className="px-3 py-2.5 font-medium">{ticket.pr_reference}</td>
                      <td className="px-3 py-2.5">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${merged ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {merged ? "Merged" : "Awaiting merge"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">{ticket.pr_raised_by_name}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground">{formatDateTime(ticket.pr_raised_at)}</td>
                      <td className="px-3 py-2.5 text-right" onClick={(event) => event.stopPropagation()}>
                        <div className="inline-flex gap-2">
                          {ticket.pr_url && <Button asChild variant="outline" size="sm"><a href={ticket.pr_url} target="_blank" rel="noreferrer">Open PR <ExternalLink className="h-3.5 w-3.5" /></a></Button>}
                          {!merged && <Button size="sm" variant="outline" onClick={() => setMergeCandidate(ticket)} disabled={Boolean(mergingId)}>Mark merged</Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <PRMergeConfirmDialog
        open={Boolean(mergeCandidate)}
        prReference={mergeCandidate?.pr_reference}
        loading={Boolean(mergingId)}
        onCancel={() => setMergeCandidate(null)}
        onConfirm={() => mergeCandidate && markMerged(mergeCandidate)}
      />
    </div>
  );
}
